import { CheckCircle2, BookOpen } from "lucide-react";
import type { Module } from "../types";
import type { LessonStatus } from "../types";

interface ModuleListItemProps {
  module: Module;
  progress: Record<string, LessonStatus>;
  index: number;
  isLast: boolean;
  onSelect: () => void;
  /** На мобиле без hover-тултипа */
  compact?: boolean;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
}

export function ModuleListItem({
  module,
  progress,
  index,
  isLast,
  onSelect,
  compact = false,
  onMouseEnter,
  onMouseLeave,
}: ModuleListItemProps) {
  const moduleLessons = module.lessons;
  const completedLessons = moduleLessons.filter((l) => progress[l.id] === "done").length;
  const moduleProgress =
    moduleLessons.length > 0 ? Math.round((completedLessons / moduleLessons.length) * 100) : 0;
  const isCompleted = moduleProgress === 100;
  const isInProgress = moduleProgress > 0 && moduleProgress < 100;

  return (
    <div className={`relative flex items-start gap-2 sm:gap-3 ${compact ? "pb-4 last:pb-0" : "pb-6 sm:pb-8 last:pb-0"}`}>
      <div className="relative flex flex-col items-center pt-0.5 sm:pt-1">
        <div
          className={`rounded-full flex items-center justify-center border-2 transition-all shadow-sm z-10 ${
            compact ? "w-8 h-8" : "w-8 h-8 sm:w-10 sm:h-10"
          } ${
            isCompleted
              ? "bg-primary border-primary text-white"
              : isInProgress
                ? "bg-orange-400 border-orange-400 text-white"
                : "bg-white border-neutral-300 text-neutral-text/50"
          }`}
        >
          {isCompleted ? (
            <CheckCircle2 className={compact ? "w-4 h-4" : "w-4 h-4 sm:w-5 sm:h-5"} />
          ) : (
            <span className={`font-bold ${compact ? "text-xs" : "text-xs sm:text-sm"}`}>{index + 1}</span>
          )}
        </div>
        {!isLast && (
          <div
            className={`w-0.5 flex-1 mt-2 transition-all ${isCompleted ? "bg-primary" : "bg-neutral-300"}`}
            style={{ minHeight: "40px" }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
        <button
          type="button"
          className={`w-full text-left rounded-lg transition-all group min-h-[44px] ${
            compact ? "p-2 -ml-2" : "p-2 sm:p-3 -ml-2 sm:-ml-3 hover:bg-neutral-light/50"
          }`}
          onClick={onSelect}
          onMouseEnter={compact ? undefined : onMouseEnter}
          onMouseLeave={compact ? undefined : onMouseLeave}
        >
          <div
            className={`text-neutral-text leading-snug font-semibold group-hover:text-primary transition-colors ${
              compact ? "text-sm mb-1" : "text-xs sm:text-sm mb-1.5 sm:mb-2"
            }`}
          >
            {module.title}
          </div>
          <div
            className={`flex flex-wrap items-center gap-1 sm:gap-2 text-neutral-text/60 mb-1 sm:mb-1.5 ${
              compact ? "gap-1.5 text-xs" : "gap-2 sm:gap-3 text-[10px] sm:text-xs"
            }`}
          >
            <span className="flex items-center gap-0.5 sm:gap-1">
              <BookOpen className={compact ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3"} />
              {moduleLessons.length} уроков
            </span>
            <span className="flex items-center gap-0.5 sm:gap-1">
              <CheckCircle2 className={compact ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3"} />
              {completedLessons}/{moduleLessons.length}
            </span>
          </div>
          <div>
            <div className={`bg-neutral-200 rounded-full overflow-hidden ${compact ? "h-1" : "h-1 sm:h-1.5"}`}>
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  isCompleted ? "bg-primary" : isInProgress ? "bg-orange-400" : "bg-neutral-300"
                }`}
                style={{ width: `${moduleProgress}%` }}
              />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
